import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Cloud, CloudCog, HardDrive, Upload, Save, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "./useApi";

export function CloudStorageSettings({ token, t }) {
  var [storageConfig, setStorageConfig] = useState({ configured: false, provider: null });
  var [storageForm, setStorageForm] = useState({ provider: "s3", s3_access_key: "", s3_secret_key: "", s3_bucket_name: "", s3_region: "eu-west-1", cloudinary_cloud_name: "", cloudinary_api_key: "", cloudinary_api_secret: "", onedrive_client_id: "", onedrive_client_secret: "", onedrive_tenant_id: "", onedrive_refresh_token: "", google_client_id: "", google_client_secret: "", google_refresh_token: "" });
  var [savingStorage, setSavingStorage] = useState(false);
  var { fetchJson, postJson, fetchApi } = useApi(token);

  useEffect(function() { fetchStorageConfig(); }, []);

  async function fetchStorageConfig() {
    try { var data = await fetchJson("/api/storage/config"); setStorageConfig(data); } catch (err) {}
  }

  async function handleSaveStorageConfig() {
    setSavingStorage(true);
    try {
      var response = await postJson("/api/storage/configure", storageForm);
      if (response.ok) { toast.success(t("settings.storageConfigured")); fetchStorageConfig(); }
      else { var error = await response.json(); toast.error(error.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
    finally { setSavingStorage(false); }
  }

  async function handleRemoveStorageConfig() {
    try {
      var response = await fetchApi("/api/storage/config", { method: "DELETE" });
      if (response.ok) { toast.success(t("settings.storageRemoved")); setStorageConfig({ configured: false, provider: null }); }
    } catch (err) { toast.error(t("common.error")); }
  }

  return (
    <Card data-testid="cloud-storage-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Cloud className="w-5 h-5 text-blue-500" />{t("settings.cloudStorage")}</CardTitle>
        <CardDescription>{t("settings.cloudStorageDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {storageConfig.configured ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <Check className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-300">{t("settings.storageActive")}</p>
                <p className="text-sm text-green-600 dark:text-green-400">{t("settings.provider")}: <span className="font-semibold uppercase">{storageConfig.provider}</span></p>
              </div>
            </div>
            <Button variant="destructive" onClick={handleRemoveStorageConfig}><X className="w-4 h-4 mr-2" />{t("settings.removeStorage")}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>{t("settings.selectProvider")}</Label>
              <Select value={storageForm.provider} onValueChange={function(v) { setStorageForm({ ...storageForm, provider: v }); }}>
                <SelectTrigger data-testid="storage-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="s3"><div className="flex items-center gap-2"><HardDrive className="w-4 h-4" /> AWS S3</div></SelectItem>
                  <SelectItem value="cloudinary"><div className="flex items-center gap-2"><Cloud className="w-4 h-4" /> Cloudinary</div></SelectItem>
                  <SelectItem value="onedrive"><div className="flex items-center gap-2"><CloudCog className="w-4 h-4" /> OneDrive</div></SelectItem>
                  <SelectItem value="google_drive"><div className="flex items-center gap-2"><Upload className="w-4 h-4" /> Google Drive</div></SelectItem>
                </SelectContent>
              </Select>
            </div>

            {storageForm.provider === "s3" && (
              <div className="space-y-4 p-4 border rounded-lg">
                <h4 className="font-medium">{t("settings.s3Credentials")}</h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label>{t("settings.accessKey")}</Label><Input type="password" value={storageForm.s3_access_key} onChange={function(e) { setStorageForm({ ...storageForm, s3_access_key: e.target.value }); }} placeholder="AKIAIOSFODNN7EXAMPLE" /></div>
                  <div><Label>{t("settings.secretKey")}</Label><Input type="password" value={storageForm.s3_secret_key} onChange={function(e) { setStorageForm({ ...storageForm, s3_secret_key: e.target.value }); }} placeholder="wJalrXUtnFEMI/K7MDENG/..." /></div>
                  <div><Label>{t("settings.bucketName")}</Label><Input value={storageForm.s3_bucket_name} onChange={function(e) { setStorageForm({ ...storageForm, s3_bucket_name: e.target.value }); }} placeholder="my-bucket" /></div>
                  <div><Label>{t("settings.region")}</Label><Select value={storageForm.s3_region} onValueChange={function(v) { setStorageForm({ ...storageForm, s3_region: v }); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="us-east-1">US East (N. Virginia)</SelectItem><SelectItem value="us-west-2">US West (Oregon)</SelectItem><SelectItem value="eu-west-1">EU (Ireland)</SelectItem><SelectItem value="eu-central-1">EU (Frankfurt)</SelectItem><SelectItem value="ap-southeast-1">Asia Pacific (Singapore)</SelectItem></SelectContent></Select></div>
                </div>
              </div>
            )}

            {storageForm.provider === "cloudinary" && (
              <div className="space-y-4 p-4 border rounded-lg">
                <h4 className="font-medium">{t("settings.cloudinaryCredentials")}</h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label>{t("settings.cloudName")}</Label><Input value={storageForm.cloudinary_cloud_name} onChange={function(e) { setStorageForm({ ...storageForm, cloudinary_cloud_name: e.target.value }); }} placeholder="my-cloud" /></div>
                  <div><Label>{t("settings.apiKey")}</Label><Input type="password" value={storageForm.cloudinary_api_key} onChange={function(e) { setStorageForm({ ...storageForm, cloudinary_api_key: e.target.value }); }} placeholder="123456789012345" /></div>
                  <div className="sm:col-span-2"><Label>{t("settings.apiSecret")}</Label><Input type="password" value={storageForm.cloudinary_api_secret} onChange={function(e) { setStorageForm({ ...storageForm, cloudinary_api_secret: e.target.value }); }} placeholder="abcdefghijk..." /></div>
                </div>
              </div>
            )}

            {storageForm.provider === "onedrive" && (
              <div className="space-y-4 p-4 border rounded-lg">
                <h4 className="font-medium">{t("settings.onedriveCredentials")}</h4>
                <p className="text-sm text-muted-foreground">{t("settings.onedriveHelp")}</p>
                <div className="grid gap-4">
                  <div><Label>{t("settings.clientId")}</Label><Input value={storageForm.onedrive_client_id} onChange={function(e) { setStorageForm({ ...storageForm, onedrive_client_id: e.target.value }); }} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" /></div>
                  <div><Label>{t("settings.clientSecret")}</Label><Input type="password" value={storageForm.onedrive_client_secret} onChange={function(e) { setStorageForm({ ...storageForm, onedrive_client_secret: e.target.value }); }} /></div>
                  <div><Label>{t("settings.tenantId")}</Label><Input value={storageForm.onedrive_tenant_id} onChange={function(e) { setStorageForm({ ...storageForm, onedrive_tenant_id: e.target.value }); }} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" /></div>
                  <div><Label>{t("settings.refreshToken")}</Label><Input type="password" value={storageForm.onedrive_refresh_token} onChange={function(e) { setStorageForm({ ...storageForm, onedrive_refresh_token: e.target.value }); }} /></div>
                </div>
              </div>
            )}

            {storageForm.provider === "google_drive" && (
              <div className="space-y-4 p-4 border rounded-lg">
                <h4 className="font-medium">{t("settings.googleDriveCredentials")}</h4>
                <p className="text-sm text-muted-foreground">{t("settings.googleDriveHelp")}</p>
                <div className="grid gap-4">
                  <div><Label>{t("settings.clientId")}</Label><Input value={storageForm.google_client_id} onChange={function(e) { setStorageForm({ ...storageForm, google_client_id: e.target.value }); }} placeholder="xxxxxxxx.apps.googleusercontent.com" /></div>
                  <div><Label>{t("settings.clientSecret")}</Label><Input type="password" value={storageForm.google_client_secret} onChange={function(e) { setStorageForm({ ...storageForm, google_client_secret: e.target.value }); }} /></div>
                  <div><Label>{t("settings.refreshToken")}</Label><Input type="password" value={storageForm.google_refresh_token} onChange={function(e) { setStorageForm({ ...storageForm, google_refresh_token: e.target.value }); }} /></div>
                </div>
              </div>
            )}

            <Button onClick={handleSaveStorageConfig} disabled={savingStorage}>
              {savingStorage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{t("settings.saveStorage")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
